import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { usePageStore } from "@/hooks/usePageStore";
import { useAppTheme } from "@/utils/theme";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ActionMenu from "@/components/ActionMenu";
import { defaultFilters, Filters } from "@/ui/content/roles/Filters";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { AlertDialog } from "@/components/AlertDialog/AlertDialog";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useTranslation } from "react-i18next";
import {
  useDeleteRole,
  useSearchRoles,
} from "@/hooks/management/useRoles.manage";
import Breadcrumbs from "@/components/Breadcrumbs";
import { toTitleCase } from "@/utils/toTitleCase";
import { Chip, Typography } from "@mui/material";
import LightTooltip from "@/components/LightTooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RoleAddModal from "@/ui/modals/roles/RoleAddModal";
import RoleUpdateModal from "@/ui/modals/roles/RoleUpdateModal";
import { RoleItem } from "@/domain/role.interface";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";

const RolesPage: FC = () => {
  const theme = useAppTheme();
  const { t } = useTranslation("roles");
  const { t: tCommon } = useTranslation("common");

  const { setPageTitle } = usePageStore();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const { userInfo, userHasAccessToCode } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const auditLogViewCalledRef = React.useRef(false);
  const { showSnackBar } = useFeedBack();
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);
  const [genericAlert, setGenericAlert] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => new Object(),
  });

  const deleteRole = useDeleteRole();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<{
    open: boolean;
    role: RoleItem | null;
  }>({
    open: false,
    role: null,
  });
  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    role: RoleItem | null;
  }>({
    open: false,
    role: null,
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const { rolesResponse, isLoadingRoles, isFetchingRoles, refetchRoles } =
    useSearchRoles({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: searchDebounce,
    });

  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const rows = useMemo(() => {
    if (!rolesResponse || (rolesResponse?.roles || []).length === 0) return [];
    return rolesResponse.roles.map((item, index) => {
      const actionMenu = [
        {
          value: t("actions.edit"),
          onClick: () => {
            setShowEditModal({ open: true, role: item });
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: t("actions.delete"),
          onClick: () => {
            setShowDeleteModal({ open: true, role: item });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];
      const row = createDataRow(index, {
        name: <div>{`${item.name}`}</div>,
        type: <div>{t(`enum.${item.type}`)}</div>,
        status: (
          <Chip
            sx={{
              bgcolor:
                item.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                item.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(item.status)}</div>}
          />
        ),
        action: <ActionMenu id={item._id} options={actionMenu} />,
      });
      return row;
    });
  }, [rolesResponse, t]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: false,
      label: t("header.name"),
      align: "left",
      width: "30%",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: t("header.type"),
      align: "left",
      width: "65%",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: t("header.status"),
      align: "left",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: true,
      label: t("header.actions"),
      align: "right",
    },
  ];

  useEffect(() => {
    setPageTitle("Roles");
  }, []);

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ROLES,
        option: OPTIONS.ROLES_LIST,
        actionCode: ACTION_CODE.WEB_ROLES_LIST_VIEW,
        action: "When entering",
        detail: "Viewed the list of roles",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo && searchDebounce) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ROLES,
        option: OPTIONS.ROLES_LIST,
        actionCode: ACTION_CODE.WEB_ROLES_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of roles",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
    }
  }, [userInfo, searchDebounce]);

  const handleDelete = async () => {
    if (!userHasAccessToCode("ROLES_DEL")) {
      setShowDeleteModal({ open: false, role: null });
      setShowPermissionDeniedModal(true);
      return;
    }

    const roleName = showDeleteModal.role?.name;

    deleteRole.mutate(showDeleteModal.role?._id as string, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ROLES,
          option: OPTIONS.ROLES_EDIT,
          actionCode: ACTION_CODE.WEB_ROLES_DELETE,
          action: "When deleting",
          detail: `Delete role ${roleName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });

        refetchRoles();
        setShowDeleteModal({ open: false, role: null });
        showSnackBar(t("messages.delete-success"), "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, role: null });
        showSnackBar(
          error?.response?.data?.message || "Failed to delete role",
          "error",
        );
      },
    });
  };

  return (
    <>
      <Box>
        <Breadcrumbs
          items={[
            {
              label: t("page-title"),
              href: "/roles",
              isLink: true,
            },
          ]}
        />
        <Box
          mt={1}
          mb={4}
          sx={{
            width: "100%",
          }}
          display="flex"
          justifyContent="space-between"
          alignItems={"center"}
        >
          <Typography
            color={theme.palette.textMain.dark2}
            variant="headlineSmall"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {t("page-title")}

            <LightTooltip title={t("messages.information")} arrow theme={theme}>
              <InfoOutlinedIcon
                sx={{
                  cursor: "pointer",
                }}
              />
            </LightTooltip>
          </Typography>

          <Box display="flex" gap="0.5rem">
            {isLoadingRoles ? (
              <SkeletonHeaderButtons />
            ) : (
              <>
                <Button
                  size={"medium"}
                  onClick={() => {
                    setShowAddModal(true);
                  }}
                  variant="contained"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    textAlign: "center",
                    height: "40px",
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.textMain.light,
                  }}
                  startIcon={<AddIcon />}
                >
                  {t("buttons.add")}
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Card>
          <CardContent>
            {/* Filters - Search */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {
              <Box
                sx={{
                  flex: { xs: "none", md: "0 0 50%" },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <SearchBox
                  theme={theme}
                  onChange={(value) => {
                    setFilters((filters) => ({
                      ...filters,
                      search: value,
                      pageNumber: 0,
                    }));
                  }}
                  value={filters.search}
                  placeHolder="Search..."
                />
              </Box>
              }
            </Box>
            <DataTable
              data={rows}
              headCells={headCells}
              order={filters.sortOrder === 1 ? "asc" : "desc"}
              orderBy={filters.sortField}
              rowsPerPage={filters.pageSize}
              page={filters.pageNumber}
              total={rolesResponse?.totalCount}
              isLoading={isLoadingRoles || isFetchingRoles}
              onChangePage={(event, newPage) => {
                setFilters((filters) => ({
                  ...filters,
                  pageNumber: newPage,
                }));
              }}
              onChangeSize={(event) => {
                setFilters((filters) => ({
                  ...filters,
                  pageNumber: 0,
                  pageSize: Number(event.target.value),
                }));
              }}
              onChangeSort={(order, orderBy) => {
                setFilters((filters) => ({
                  ...filters,
                  pageNumber: 0,
                  sortField: orderBy as string,
                  sortOrder: order === "asc" ? 1 : -1,
                }));
              }}
              messageEmptyData={t("messages.empty-list")}
            />
          </CardContent>
        </Card>
      </Box>

      {showAddModal && (
        <RoleAddModal
          open={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            refetchRoles();
          }}
        />
      )}

      {showEditModal.open && showEditModal.role && (
        <RoleUpdateModal
          open={showEditModal.open}
          onClose={() => {
            setShowEditModal({ open: false, role: null });
            refetchRoles();
          }}
          role={showEditModal.role}
        />
      )}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          onCancel={() => {
            setShowDeleteModal({ open: false, role: null });
          }}
          onConfirm={handleDelete}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}

      {genericAlert.open ? (
        <AlertDialog
          open={genericAlert.open}
          onConfirm={() => {
            setGenericAlert((prevState) => ({
              ...prevState,
              open: false,
            }));
          }}
          onCancel={() => {
            setGenericAlert((prevState) => ({
              ...prevState,
              open: false,
            }));
          }}
          title={genericAlert.title}
          description={genericAlert.message}
          confirmText="Close"
        />
      ) : null}

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </>
  );
};

export default RolesPage;
